// src/pages/authn/OAuthConsentsPage.tsx
// Manage granted OAuth2 consents — view and revoke.

import { useState, useEffect } from 'react';
import { observer } from '@react';
import { useAuthn, useI18n } from '@react/hooks';
import {
  Button, Card, CardHeader, CardTitle, CardDescription, CardContent, ErrorAlert,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty, Badge,
} from '@react/components';
import type { OAuth2Consent } from '@core/types/authn';

export const OAuthConsentsPage = observer(() => {
  const authnStore = useAuthn();
  const { t } = useI18n();

  const [consents, setConsents] = useState<OAuth2Consent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    loadConsents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConsents = async () => {
    setLoading(true);
    try {
      const resp = await authnStore.ListOAuth2Consents();
      setConsents(resp.consents?.items ?? []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (clientId: string) => {
    if (!confirm(t('authn.revokeConsentConfirm'))) return;
    try {
      await authnStore.RevokeOAuth2Consent(clientId);
      setConsents((prev) => prev.filter((c) => c.client?.id !== clientId));
    } catch (err) {
      setError(err);
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('authn.consentsTitle')}</h1>

      {error ? <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('authn.grantedConsents')}</CardTitle>
          <CardDescription>{t('authn.grantedConsentsDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('authn.application')}</TableHead>
                <TableHead>{t('authn.scopes')}</TableHead>
                <TableHead>{t('common.createdAt')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8">{t('common.loading')}</TableCell>
                </TableRow>
              ) : consents.length === 0 ? (
                <TableEmpty colSpan={4}>
                  <span className="table-empty-message">{t('authn.noConsents')}</span>
                  <p className="mt-1 text-sm text-muted-foreground">{t('authn.consentsSubtitle')}</p>
                </TableEmpty>
              ) : (
                consents.map((consent) => (
                  <TableRow key={consent.client?.id ?? consent.entity?.id}>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium text-foreground">{consent.client?.type_name || consent.client?.id}</p>
                        <p className="text-xs text-muted-foreground font-mono">{consent.client?.id}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(consent.granted_scopes?.items ?? []).map((scope: string) => (
                          <Badge key={scope} variant="secondary" size="sm">{scope}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{consent.granted_at || ''}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleRevoke(consent.client?.id ?? '')}>
                        {t('authn.revokeConsent')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
});

export default OAuthConsentsPage;
